import type { Route } from 'next';
import { match } from 'ts-pattern';

import { NMRiumTooltip } from '../../components/marketing/NMRiumTooltip';

import { getPriceAndDiscount } from './getPriceAndDiscount';
import type {
  ComparableFeature,
  LicenseOption,
  LicenseType,
  PriceVariables,
  Tier,
} from './types';

const SITE_LICENSE = 'Site license';
const PRICE_PER_USER = 'Priced per user';
const AUTO_IMPORT = 'Automatic importation';
const ON_PREMISES = 'Hosted on your infrastructure';
const DEDICATED_WEBINAR = 'Extended webinar';
const CUSTOM_DOMAIN = 'Custom domain name';
const DATA_PRIVACY = 'Privacy for your data';
const SPECTRA_DB = 'Private spectra database';
const LICENSE_NAME: Record<LicenseType, string> = {
  individual: 'For individuals',
  commercial: 'For small businesses',
  academic: 'For academia',
};

export const freeTier: Tier = {
  id: 'free',
  name: 'Explore',
  type: ({ licenseType }) => LICENSE_NAME[licenseType],
  price: () => ({ value: 'Free', suffix: null, firstYearDiscount: null }),
  cta: () => ({
    value: 'Try it now',
    href: '/nmrium/demo',
    soft: true,
  }),
  seats: () => false,
  comparisonFeatures: {
    privacy: null,
    users: null,
    support: null,
    formation: null,
    infra: null,
  },
  additionalFeatures: [],
  description:
    'Use NMRium for free on our website. Usage is restricted to public data and non-commercial applications.',
  featured: false,
};

export const proTier: Tier = {
  id: 'professional',
  name: 'Professional',
  type: ({ licenseType }) => LICENSE_NAME[licenseType],
  seats: () => false,
  price: (variables) => {
    return {
      ...getPriceAndDiscount('professional', variables),
      suffix: `CHF / year`,
    };
  },
  description: (
    <div>
      Get the most out of NMRium with our{' '}
      <NMRiumTooltip
        delay={100}
        content={
          <div className="max-w-sm space-y-3 px-1 py-2">
            <p>
              The software will be installed directly onto servers located
              within your institutional network and managed by your own IT
              staff.
            </p>
          </div>
        }
      >
        <span className="underline whitespace-nowrap">on-premise solution</span>
      </NMRiumTooltip>{' '}
      including a database for your NMR spectra.
    </div>
  ),
  additionalFeatures: [
    {
      value: 'Getting started webinar',
      icon: 'double-check',
    },
    {
      value: SPECTRA_DB,
      icon: 'double-check',
    },
    {
      value: AUTO_IMPORT,
      icon: 'double-check',
    },
  ],
  comparisonFeatures: {
    privacy: DATA_PRIVACY,
    users: ({ licenseType }) =>
      match(licenseType)
        .with('individual', () => PRICE_PER_USER)
        .with('commercial', () => 'Up to 10 nominative users')
        .with('academic', () => ({ value: SITE_LICENSE, emphasis: true }))
        .exhaustive(),
    support: '48-hour support response time',
    infra: () => ON_PREMISES,
    formation: null,
  },
  featured: false,
  cta: (variables) => ({
    value: 'Request a quote',
    href: `pricing/request/?tier=professional&${getPriceVariableParams(variables)}` as Route,
  }),
};

export const starterTier: Tier = {
  id: 'starter',
  name: 'Starter',
  type: ({ licenseType }) => LICENSE_NAME[licenseType],
  seats: (variables) => variables.licenseType === 'commercial',
  price: (variables) => {
    const { licenseType } = variables;

    return {
      ...getPriceAndDiscount('starter', variables),
      suffix: `CHF / ${licenseType === 'individual' ? 'user / ' : ''}year`,
    };
  },
  description: (
    <div>
      Get started with a simple and affordable plan. Use NMRium{' '}
      <NMRiumTooltip
        delay={100}
        content={
          <div className="max-w-sm space-y-3 px-1 py-2">
            <p className="max-w-sm">
              NMRium will be hosted on one of our secure servers to which none
              of your NMR data will be sent.
            </p>
            <p>
              You can choose the domain name from which it should be served,
              e.g., https://nmrium.[your_organization].com. For more security,
              you can limit the range of IP addresses from which it will be
              accessible.
            </p>
            <p>
              Our team will handle all maintenance and updates, ensuring that
              the service remains up-to-date and running smoothly at all times.
            </p>
          </div>
        }
      >
        <span className="underline">on a custom domain</span>
      </NMRiumTooltip>{' '}
      with your private or commercial data.
    </div>
  ),
  additionalFeatures: [],
  comparisonFeatures: {
    privacy: DATA_PRIVACY,
    users: ({ licenseType, userPlan }) =>
      match(licenseType)
        .with('individual', () => PRICE_PER_USER)
        .with(
          'commercial',
          () => `Up to ${userPlan} nominative user${userPlan === 1 ? '' : 's'}`,
        )
        .with('academic', () => ({ value: SITE_LICENSE, emphasis: true }))
        .exhaustive(),
    support: 'Support via email',
    infra: () => CUSTOM_DOMAIN,
    formation: null,
  },
  featured: false,
  cta: (variables) => ({
    value: 'Request a quote',
    href: `pricing/request/?tier=starter&${getPriceVariableParams(variables)}` as Route,
  }),
};

export const enterpriseTier: Tier = {
  name: 'Enterprise',
  type: () => '',
  id: 'enterprise',
  seats: () => false,
  price: () => ({
    value: 'Custom price',
    suffix: null,
    firstYearValue: null,
    firstYearDiscount: null,
  }),
  description:
    'Dedicated features, support and infrastructure for your organization.',
  additionalFeatures: [
    SPECTRA_DB,
    AUTO_IMPORT,
    {
      value: 'ELN integration on request',
      icon: 'double-check',
    },
    {
      value: 'Single sign-on (SSO)',
      icon: 'double-check',
    },
  ],
  comparisonFeatures: {
    privacy: DATA_PRIVACY,
    users: { value: SITE_LICENSE, emphasis: true },
    support: {
      value: 'Custom support response time',
      emphasis: true,
    },
    infra: ON_PREMISES,
    formation: DEDICATED_WEBINAR,
  },
  featured: true,
  cta: (variables) => ({
    value: 'Contact sales',
    href: `pricing/request/?tier=enterprise&${getPriceVariableParams(variables)}` as Route,
  }),
};

export const commercialTiers = [starterTier, proTier, enterpriseTier];

export const comparableFeatures: ComparableFeature[] = [
  'users',
  'privacy',
  'support',
  'infra',
  'formation',
];

export const licenseTypeOptions: LicenseOption[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'academic', label: 'Academic' },
];

export const paidLicenseTypeOptions = licenseTypeOptions.filter(
  (license) => license.value !== 'individual',
);

function getPriceVariableParams(variables: PriceVariables) {
  if (!variables.userPlan) {
    return `licenseType=${variables.licenseType}`;
  }

  return `licenseType=${variables.licenseType}&users=${variables.userPlan}`;
}
